import * as React from "react"
import { useQuery } from "@tanstack/react-query"
import { Bot, ShieldAlert, User } from "lucide-react"

import { DataState } from "@/components/DataState"
import { PageHeader } from "@/components/PageHeader"
import { Toolbar, ToolbarField, ToolbarNote } from "@/components/Toolbar"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { formatDateTime } from "@/lib/format"
import { cn } from "@/lib/utils"

import { Advertencias } from "@/routes/holding/Advertencias"
import { fetchPista, fetchResumen, type CambioDeCampo } from "@/routes/auditoria/api"
import { t as traducir } from "@/lib/i18n"

/**
 * LA PISTA DE AUDITORIA: quien cambio que, cuando y por que.
 *
 * El sistema lleva años escribiendola —cada edicion con autor, fecha y motivo,
 * en una tabla que un trigger impide reescribir— y no habia forma de leerla.
 * Cumplia la parte cara de la promesa y no la que la vuelve util: un auditor
 * externo no entra por psql.
 *
 * DOS DECISIONES DE LA PANTALLA:
 *
 * «Solo lo que alguien decidio» viene MARCADO por defecto. Sin ese filtro, las
 * 687 altas del importador entierran las cuatro correcciones con motivo, que es
 * lo unico que un auditor mira en la primera pasada. El resto sigue a un clic.
 *
 * Se muestra el DIFF, no el registro. En una tabla de treinta columnas, pintar
 * el antes y el despues completos obliga a compararlos a ojo — que es
 * exactamente el trabajo que esta pantalla existe para ahorrar.
 */

const TABLAS = [
  "", "journal_entry", "invoice", "document", "contract", "party",
  "financial_fact", "employee", "period", "treatment_rule", "bank_account",
  "group_ownership", "consolidation_run", "intercompany_pair",
]

const ACCIONES = ["", "create", "update", "delete", "override", "reclasificar",
                  "consolidar", "denegado"]

export function AuditoriaPage() {
  const [tabla, setTabla] = React.useState("")
  const [accion, setAccion] = React.useState("")
  const [usuario, setUsuario] = React.useState("")
  const [soloConMotivo, setSoloConMotivo] = React.useState(true)

  const resumen = useQuery({
    queryKey: ["auditoria", "resumen"],
    queryFn: () => fetchResumen(30),
  })
  const pista = useQuery({
    queryKey: ["auditoria", "pista", tabla, accion, usuario, soloConMotivo],
    queryFn: () => fetchPista({ tabla, accion, usuario, soloConMotivo }),
  })

  const r = resumen.data
  return (
    <div className="space-y-3">
      <PageHeader
        eyebrow="Plataforma"
        title={traducir("Pista de auditoría")}
        description={traducir("Cada cambio con su autor, su fecha y su motivo, en una tabla que la base impide reescribir: un trigger rechaza UPDATE y DELETE. Una corrección es una entrada nueva, nunca una edición del pasado.")}
      />

      {r ? <Advertencias advertencias={r.advertencias} /> : null}

      {r ? (
        <div className="grid gap-3 md:grid-cols-3">
          <Cifra etiqueta={`Escrituras en ${r.dias} días`} valor={r.total} />
          <Cifra
            etiqueta={traducir("De procesos de servicio")}
            valor={r.sin_autor}
            nota={traducir("Importadores, seeds y jobs. No son acciones de una persona.")}
          />
          <Cifra
            etiqueta={traducir("Intentos denegados")}
            valor={r.denegados}
            enRojo={r.denegados > 0}
            nota={traducir("Saber quién trató de hacer qué vale tanto como saber quién lo hizo.")}
          />
        </div>
      ) : null}

      <Toolbar>
        <ToolbarField label={traducir("Tabla")} htmlFor="aud-tabla">
          <select
            id="aud-tabla"
            value={tabla}
            onChange={(e) => setTabla(e.target.value)}
            className="h-7 w-[11rem] rounded-md border bg-surface px-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {TABLAS.map((t) => (
              <option key={t || "todas"} value={t}>
                {t || "Todas"}
              </option>
            ))}
          </select>
        </ToolbarField>

        <ToolbarField label={traducir("Acción")} htmlFor="aud-accion">
          <select
            id="aud-accion"
            value={accion}
            onChange={(e) => setAccion(e.target.value)}
            className="h-7 w-[9rem] rounded-md border bg-surface px-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {ACCIONES.map((a) => (
              <option key={a || "todas"} value={a}>
                {a || "Todas"}
              </option>
            ))}
          </select>
        </ToolbarField>

        <ToolbarField label={traducir("Autor")} htmlFor="aud-usuario">
          <Input
            id="aud-usuario"
            value={usuario}
            onChange={(e) => setUsuario(e.target.value)}
            placeholder={traducir("correo, parcial")}
            className="h-7 w-[13rem]"
          />
        </ToolbarField>

        <div className="flex items-center gap-2 self-end pb-1">
          <Checkbox
            id="aud-motivo"
            checked={soloConMotivo}
            onCheckedChange={(v) => setSoloConMotivo(Boolean(v))}
          />
          <Label htmlFor="aud-motivo" className="cursor-pointer">
            {traducir("Solo lo que alguien decidió")}</Label>
        </div>

        <ToolbarNote icon={ShieldAlert} className="ml-auto">
          {traducir("Con el filtro quitado salen también las altas automáticas, que son la mayoría y entierran lo que sí se decidió.")}</ToolbarNote>
      </Toolbar>

      <DataState
        loading={pista.isLoading}
        error={pista.error}
        empty={!pista.isLoading && (pista.data?.entradas.length ?? 0) === 0}
        emptyMessage={
          soloConMotivo
            ? "Nadie ha corregido nada con motivo en este alcance."
            : "No hay escrituras con estos filtros."
        }
        emptyHint={
          soloConMotivo
            ? "Es buena noticia: significa que no hizo falta corregir a mano. Quita el filtro para ver también lo que cargaron los importadores."
            : undefined
        }
        onRetry={() => void pista.refetch()}
        columns={[16, 12, 14, 16, 22, 20]}
      >
        <WorkPanel
          title={traducir("Qué se tocó")}
          meta={
            pista.data
              ? `${pista.data.total_devuelto}${pista.data.hay_mas ? "+" : ""} entrada(s)`
              : undefined
          }
          footnote={pista.data?.nota}
        >
          <Table className="table-dense">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[10.5rem]">{traducir("Cuándo")}</TableHead>
                <TableHead className="w-24">{traducir("Acción")}</TableHead>
                <TableHead className="w-[10rem]">{traducir("Tabla")}</TableHead>
                <TableHead className="w-[13rem]">{traducir("Quién")}</TableHead>
                <TableHead className="w-[20rem]">{traducir("Motivo")}</TableHead>
                <TableHead>{traducir("Qué cambió")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pista.data?.entradas.map((e) => (
                <TableRow key={e.id}>
                  <TableCell className="code truncate text-muted-foreground">
                    {formatDateTime(e.cuando)}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        e.accion === "denegado"
                          ? "destructive-soft"
                          : e.accion === "create"
                            ? "success-soft"
                            : "outline"
                      }
                      estado
                    >
                      {e.accion}
                    </Badge>
                  </TableCell>
                  <TableCell className="code truncate" title={e.tabla}>
                    {e.tabla}
                  </TableCell>
                  <TableCell className="truncate" title={e.autor_email ?? undefined}>
                    <span className="inline-flex items-center gap-1.5">
                      {e.sin_autor ? (
                        <Bot className="h-3 w-3 shrink-0 text-muted-foreground" />
                      ) : (
                        <User className="h-3 w-3 shrink-0 text-brand" />
                      )}
                      <span className={cn(e.sin_autor && "text-muted-foreground")}>
                        {e.autor ?? "proceso de servicio"}
                      </span>
                    </span>
                  </TableCell>
                  <TableCell className="clamp-2 text-2xs" title={e.motivo ?? undefined}>
                    {e.motivo ?? <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell>
                    <Diff cambios={e.cambios} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </WorkPanel>
      </DataState>
    </div>
  )
}

/** El antes → después de lo que cambió. Nunca el registro entero. */
function Diff({ cambios }: { cambios: CambioDeCampo[] }) {
  if (cambios.length === 0) {
    return <span className="text-2xs text-muted-foreground">{traducir("sin detalle guardado")}</span>
  }
  const visibles = cambios.slice(0, 3)
  return (
    <div className="space-y-px">
      {visibles.map((c) => (
        <div key={c.campo} className="flex items-baseline gap-1.5 text-2xs leading-4">
          <span className="code shrink-0 text-muted-foreground">{c.campo}</span>
          {c.es_alta ? (
            <span className="text-success">+ {texto(c.despues)}</span>
          ) : c.es_baja ? (
            <span className="text-destructive">− {texto(c.antes)}</span>
          ) : (
            <>
              <span className="truncate text-muted-foreground line-through">
                {texto(c.antes)}
              </span>
              <span aria-hidden="true" className="shrink-0 text-border-strong">
                →
              </span>
              <span className="truncate font-medium">{texto(c.despues)}</span>
            </>
          )}
        </div>
      ))}
      {cambios.length > visibles.length ? (
        <div className="text-2xs text-muted-foreground">
          y {cambios.length - visibles.length}{" "}{traducir("campo(s) más")}</div>
      ) : null}
    </div>
  )
}

function texto(v: unknown): string {
  if (v === null || v === undefined) return "—"
  const s = typeof v === "string" ? v : JSON.stringify(v)
  return s.length > 42 ? `${s.slice(0, 41)}…` : s
}

function Cifra({
  etiqueta,
  valor,
  nota,
  enRojo,
}: {
  etiqueta: string
  valor: number
  nota?: string
  enRojo?: boolean
}) {
  return (
    <div className="panel px-4 py-3">
      <div className="eyebrow">{etiqueta}</div>
      <div className={cn("mt-1 text-xl", enRojo ? "cifra-atencion" : "cifra")}>{valor}</div>
      {nota ? (
        <p className="mt-1 text-2xs leading-[0.95rem] text-muted-foreground">{nota}</p>
      ) : null}
    </div>
  )
}
